import { getUserErrorMessage } from '@/shared/utils/userErrorMessage';
import { useIntl } from '@umijs/max';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Spin,
  Statistic,
} from 'antd';
import React from 'react';
import { useDangerConfirm, useToast } from '@/hooks';
import {
  adminGetUserCredits,
  adminGrantUserCredits,
  type UserCreditBalance,
} from '@/services/auth/api';
import { extractErrorMessage } from '@/services/auth/errorCodes';

interface Props {
  userId: number;
  /** 上分成功后回调,让外层刷新「充值记录」等 */
  onGranted?: () => void;
}

const CreditPanel: React.FC<Props> = ({ userId, onGranted }) => {
  const intl = useIntl();
  const toast = useToast();
  const confirm = useDangerConfirm();
  const [form] = Form.useForm<{ amount: number; note?: string }>();

  const [loading, setLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [balance, setBalance] = React.useState<UserCreditBalance | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const resp = await adminGetUserCredits(userId);
      if (resp?.success && resp?.data) setBalance(resp.data.credits);
    } catch {
      // fail-soft:余额拉取失败不打断,留默认 0
    } finally {
      setLoading(false);
    }
  }, [userId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const doGrant = async (amount: number, note?: string) => {
    setSubmitting(true);
    try {
      const resp = await adminGrantUserCredits(userId, { amount, note });
      if (resp?.success && resp?.data) {
        setBalance(resp.data);
        form.resetFields();
        onGranted?.();
        toast.success(
          intl.formatMessage({
            id: 'pages.users.credit.done',
            defaultMessage: '已调整',
          }),
        );
      } else {
        toast.error(
          getUserErrorMessage(resp),
        );
      }
    } catch (e: unknown) {
      toast.error(
        extractErrorMessage(
          e,
          intl.formatMessage({
            id: 'pages.users.credit.failed',
            defaultMessage: '调整失败',
          }),
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onFinish = (values: { amount: number; note?: string }) => {
    const amount = Math.trunc(values.amount);
    if (!amount) return;
    const note = values.note?.trim() || undefined;
    if (amount < 0) {
      confirm({
        title: intl.formatMessage(
          {
            id: 'pages.users.credit.deductConfirm',
            defaultMessage: '确认扣除 {amount} Credit?',
          },
          { amount: -amount },
        ),
        onOk: () => doGrant(amount, note),
      });
      return;
    }
    void doGrant(amount, note);
  };

  return (
    <Spin spinning={loading}>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title={intl.formatMessage({
                id: 'pages.users.credit.balance',
                defaultMessage: '当前余额',
              })}
              value={balance?.credits ?? 0}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title={intl.formatMessage({
                id: 'pages.users.credit.totalGrant',
                defaultMessage: '累计充值',
              })}
              value={balance?.totalGrant ?? 0}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title={intl.formatMessage({
                id: 'pages.users.credit.totalSpend',
                defaultMessage: '累计消费',
              })}
              value={balance?.totalSpend ?? 0}
            />
          </Col>
        </Row>
      </Card>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="amount"
          label={intl.formatMessage({
            id: 'pages.users.credit.amount',
            defaultMessage: '调整数额',
          })}
          extra={intl.formatMessage({
            id: 'pages.users.credit.amountHint',
            defaultMessage: '正数充值,负数扣除',
          })}
          rules={[{ required: true }]}
        >
          <InputNumber
            style={{ width: 240 }}
            precision={0}
            step={50}
            placeholder="50"
          />
        </Form.Item>
        <Form.Item
          name="note"
          label={intl.formatMessage({
            id: 'pages.users.credit.note',
            defaultMessage: '备注',
          })}
        >
          <Input maxLength={200} style={{ maxWidth: 360 }} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={submitting}>
            {intl.formatMessage({
              id: 'pages.users.credit.submit',
              defaultMessage: '确认',
            })}
          </Button>
        </Space>
      </Form>
    </Spin>
  );
};

export default CreditPanel;
